import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Filter } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";

const EMPTY_LABEL = "(Empty)";

interface UseColumnFiltersReturn {
  filters: Record<string, Set<string>>;
  setFilter: (column: string, values: Set<string>) => void;
  clearFilter: (column: string) => void;
  clearAll: () => void;
  isActive: (column: string) => boolean;
  hasAnyActive: () => boolean;
  applyFilters: <T>(items: T[], getters: Record<string, (item: T) => string>) => T[];
}

export function useColumnFilters(resetKey: string): UseColumnFiltersReturn {
  const [filters, setFilters] = useState<Record<string, Set<string>>>({});
  const prevResetKey = useRef(resetKey);

  useEffect(() => {
    if (prevResetKey.current !== resetKey) {
      setFilters({});
      prevResetKey.current = resetKey;
    }
  }, [resetKey]);

  const setFilter = useCallback((column: string, values: Set<string>) => {
    setFilters(prev => ({ ...prev, [column]: values }));
  }, []);

  const clearFilter = useCallback((column: string) => {
    setFilters(prev => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setFilters({});
  }, []);

  const isActive = useCallback((column: string) => {
    return column in filters;
  }, [filters]);

  const hasAnyActive = useCallback(() => {
    return Object.keys(filters).length > 0;
  }, [filters]);

  const applyFilters = useCallback(<T,>(items: T[], getters: Record<string, (item: T) => string>): T[] => {
    const activeEntries = Object.entries(filters);
    if (activeEntries.length === 0) return items;

    return items.filter(item => {
      return activeEntries.every(([column, selectedValues]) => {
        const getter = getters[column];
        if (!getter) return true;
        const value = getter(item);
        return selectedValues.has(value);
      });
    });
  }, [filters]);

  return { filters, setFilter, clearFilter, clearAll, isActive, hasAnyActive, applyFilters };
}

interface ColumnFilterProps {
  columnKey: string;
  values: string[];
  selectedValues: Set<string> | undefined;
  onSelectionChange: (column: string, values: Set<string>) => void;
  onClear: (column: string) => void;
  isActive: boolean;
  align?: "start" | "center" | "end";
  maxHeight?: string;
}

export function ColumnFilter({
  columnKey,
  values,
  selectedValues,
  onSelectionChange,
  onClear,
  isActive,
  align = "start",
  maxHeight = "13rem",
}: ColumnFilterProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const sortedValues = useMemo(() => {
    return [...values].sort((a, b) => {
      if (a === "" && b !== "") return 1;
      if (b === "" && a !== "") return -1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
  }, [values]);

  const filteredValues = useMemo(() => {
    if (!search.trim()) return sortedValues;
    const q = search.toLowerCase();
    return sortedValues.filter(v => {
      const label = v === "" ? EMPTY_LABEL : v;
      return label.toLowerCase().includes(q);
    });
  }, [sortedValues, search]);

  const handleToggle = (value: string) => {
    const current = selectedValues ?? new Set(values);
    const next = new Set(current);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    if (next.size === values.length) {
      onClear(columnKey);
    } else {
      onSelectionChange(columnKey, next);
    }
  };

  const handleSelectAll = () => {
    if (search.trim()) {
      const current = selectedValues ?? new Set(values);
      const next = new Set(current);
      filteredValues.forEach(v => next.add(v));
      if (next.size >= values.length) {
        onClear(columnKey);
      } else {
        onSelectionChange(columnKey, next);
      }
    } else {
      onClear(columnKey);
    }
  };

  const handleClearAll = () => {
    if (search.trim()) {
      const current = selectedValues ?? new Set(values);
      const next = new Set(current);
      filteredValues.forEach(v => next.delete(v));
      onSelectionChange(columnKey, next);
    } else {
      onSelectionChange(columnKey, new Set<string>());
    }
  };

  const effectiveSelected = selectedValues ?? new Set(values);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center justify-center rounded-sm p-0.5 transition-colors cursor-pointer",
            isActive
              ? "text-primary"
              : "text-muted-foreground/50 hover:text-muted-foreground"
          )}
          data-testid={`button-column-filter-${columnKey}`}
          aria-label={`Filter ${columnKey}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Filter className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-0"
        align={align}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2 space-y-2">
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
            data-testid={`input-column-filter-search-${columnKey}`}
          />
          <div className="flex items-center justify-between gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="h-6 text-xs px-2"
              data-testid={`button-column-filter-select-all-${columnKey}`}
            >
              Select All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-6 text-xs px-2"
              data-testid={`button-column-filter-clear-all-${columnKey}`}
            >
              Clear All
            </Button>
          </div>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight }}>
          <div className="px-2 pb-2 space-y-0.5">
            {filteredValues.length === 0 ? (
              <div className="py-3 text-center text-xs text-muted-foreground" data-testid={`text-column-filter-no-results-${columnKey}`}>
                No matching values
              </div>
            ) : (
              filteredValues.map((value) => {
                const label = value === "" ? EMPTY_LABEL : value;
                const checked = effectiveSelected.has(value);
                return (
                  <label
                    key={value}
                    className="flex items-center gap-2 px-1 py-1 rounded-sm hover-elevate cursor-pointer text-sm"
                    data-testid={`label-column-filter-value-${columnKey}-${value || "empty"}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => handleToggle(value)}
                      data-testid={`checkbox-column-filter-${columnKey}-${value || "empty"}`}
                    />
                    <span className={cn("truncate", value === "" && "italic text-muted-foreground")}>
                      {label}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface FilterableTableHeadProps {
  label: string;
  columnKey: string;
  values: string[];
  selectedValues: Set<string> | undefined;
  onSelectionChange: (column: string, values: Set<string>) => void;
  onClear: (column: string) => void;
  isActive: boolean;
  className?: string;
  filterAlign?: "start" | "center" | "end";
  filterMaxHeight?: string;
  children?: React.ReactNode;
}

export function FilterableTableHead({
  label,
  columnKey,
  values,
  selectedValues,
  onSelectionChange,
  onClear,
  isActive,
  className,
  filterAlign,
  filterMaxHeight,
  children,
}: FilterableTableHeadProps) {
  return (
    <TableHead className={className}>
      <div className="flex items-center gap-1">
        {children ?? <span>{label}</span>}
        <ColumnFilter
          columnKey={columnKey}
          values={values}
          selectedValues={selectedValues}
          onSelectionChange={onSelectionChange}
          onClear={onClear}
          isActive={isActive}
          align={filterAlign}
          maxHeight={filterMaxHeight}
        />
      </div>
    </TableHead>
  );
}
